import { Page, Text, View } from '@react-pdf/renderer'
import { Button, Form, Input, message } from 'antd'
import GeckoLogo from 'features/reporting/pdfGeneration/components/shared/GeckoLogo'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { SectionHeader } from 'pages/reporting/reports/externalVisual/components/VisualInspectionSectionHeader'
import { FindingsData } from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useVisualReportOps } from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useCallback, useMemo } from 'react'

const requiredFindingsFields = ['findings']

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useVisualReportOps()
  const data = useMemo(() => formData.findingsData, [formData.findingsData])

  const isPrelim = useMemo(
    () =>
      !(
        formData.signoff?.reviewerSignoff && formData.signoff?.supervisorSignoff
      ),
    [formData.signoff?.reviewerSignoff, formData.signoff?.supervisorSignoff]
  )

  return (
    <Page size='A4' style={PDFStyles.page} id='findings' wrap={false}>
      {/* Hidden text for page tracking */}
      <Text
        render={({ pageNumber }) => {
          onRender({ id: 'findings', pageNumber })
          return ''
        }}
      />

      <View style={PDFStyles.content}>
        {/* Header with Gecko logo */}
        <View style={PDFStyles.coverHeader}>
          <GeckoLogo />
          <View style={PDFStyles.coverHeaderContent}>
            <Text style={PDFStyles.coverHeaderText}>
              Checklist Inspection Report
            </Text>
          </View>
        </View>

        {/* Section Header */}
        <SectionHeader title='Findings' />

        {/* Content */}
        <View style={{ marginTop: 10 }}>
          {data.findings ? (
            <Text style={{ fontSize: 10, lineHeight: 1.5 }}>
              {data.findings}
            </Text>
          ) : (
            <Text style={{ fontSize: 10 }}>No data to display.</Text>
          )}
        </View>
      </View>

      <PageFooter
        revisionNumber={formData.revisionMetadata.revisionNumber}
        prelim={isPrelim}
      />
    </Page>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<FindingsData>()
  const { formData, handleFindingsFinish } = useVisualReportOps()

  const onFinish = useCallback(
    async (values: FindingsData) => {
      try {
        await handleFindingsFinish(values.findings || '')
        message.success('Findings saved successfully!')
      } catch {
        message.error('Failed to save findings')
      }
    },
    [handleFindingsFinish]
  )

  const initialValues = React.useMemo(
    () => formData.findingsData,
    [formData.findingsData]
  )

  return (
    <StyledForm<FindingsData>
      form={form}
      name='findingsForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.Item
        name='findings'
        label='Findings'
        rules={[
          { required: true, message: 'Please enter the inspection findings' },
        ]}>
        <Input.TextArea
          rows={6}
          placeholder='Enter inspection findings summary...'
        />
      </Form.Item>

      <Form.Item>
        <Button type='primary' htmlType='submit'>
          Save Findings
        </Button>
      </Form.Item>
    </StyledForm>
  )
}

export const FindingsSection: ReportSection = {
  RenderReact,
  required: true,
  RenderPDF,
  id: 'findings',
  description: 'Inspection findings summary',
  title: 'Findings',
  requiredFields: requiredFindingsFields,
}

export const FindingsSectionPDF = RenderPDF
